import React, { useState, useEffect } from 'react';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Card from '../../ui/Card';
import { flushRedisCache } from '../adminApiService';
import toast from 'react-hot-toast';

const TournamentSettingsTab = ({ 
  tournament, 
  canModify, 
  updateMutation, 
  onChanges 
}) => {
  const [settings, setSettings] = useState({
    // Betting settings
    bettingEnabled: true,
    minBetAmount: 10,
    maxBetAmount: 1000,
    allowMultipleBets: false,
    bettingDeadline: '',
    
    // Display settings
    showLeaderboard: true,
    showPlayerStats: true,
    showRealTimeUpdates: true,
    publicTournament: true,
    
    // Notification settings
    emailNotifications: true,
    pushNotifications: false,
    reminderNotifications: true,
    
    // Advanced settings
    timeZone: 'America/New_York',
    scoringFrequency: 'real-time', // real-time, hourly, daily
    prizeDistributionType: 'automatic', // automatic, manual
    
    // Tournament specific
    maxPlayersPerUser: 3,
    allowPlayerTrades: false,
    tradingDeadline: '',
    
    // Metadata
    tags: [],
    category: 'professional',
    difficulty: 'intermediate'
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [isFlushingCache, setIsFlushingCache] = useState(false);

  // Initialize settings from tournament data
  useEffect(() => {
    if (tournament) {
      // In a real app, these would come from tournament.settings or API
      // For now, using defaults with some tournament-specific data
      setSettings({
        bettingEnabled: tournament.status === 'ACTIVE',
        minBetAmount: 10,
        maxBetAmount: 1000,
        allowMultipleBets: false,
        bettingDeadline: tournament.startDate ? new Date(tournament.startDate).toISOString().slice(0, 16) : '',
        showLeaderboard: true,
        showPlayerStats: true,
        showRealTimeUpdates: true,
        publicTournament: true,
        emailNotifications: true,
        pushNotifications: false,
        reminderNotifications: true,
        timeZone: 'America/New_York',
        scoringFrequency: 'real-time',
        prizeDistributionType: 'automatic',
        maxPlayersPerUser: 3,
        allowPlayerTrades: false,
        tradingDeadline: '',
        tags: [],
        category: 'professional',
        difficulty: 'intermediate'
      });
      setHasChanges(false);
    }
  }, [tournament]);

  // Notify parent of changes
  useEffect(() => {
    onChanges(hasChanges);
  }, [hasChanges, onChanges]);

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      // In a real app, this would be a separate API call to update tournament settings
      await updateMutation.mutateAsync({
        id: tournament.id,
        settings: settings
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving tournament settings:', error);
    }
  };

  const handleReset = () => {
    // Reset to original tournament settings
    setHasChanges(false);
  };

  const handleFlushCache = async () => {
    if (window.confirm('This will flush ALL Redis cache data. This action cannot be undone and may temporarily impact performance. Continue?')) {
      setIsFlushingCache(true);
      
      try {
        const result = await flushRedisCache();
        toast.success(`Cache flushed successfully! Keys before: ${result.before?.keys || 'N/A'}, after: ${result.after?.keys || 'N/A'}`);
        console.log('Cache flush result:', result);
      } catch (error) {
        console.error('Error flushing cache:', error);
        toast.error('Failed to flush cache: ' + (error.message || 'Unknown error'));
      } finally {
        setIsFlushingCache(false);
      }
    }
  };

  return (
    <div className="admin-tournament-settings-tab">
      <div className="tab-header">
        <h2>Tournament Settings</h2>
        <p>Configure advanced tournament options and behavior</p>
      </div>

      <div className="settings-content">
        <div className="settings-grid">
          
          {/* Betting Settings */}
          <Card className="settings-section">
            <h3>Betting Configuration</h3>
            
            <div className="setting-group">
              <div className="setting-item">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={settings.bettingEnabled}
                    onChange={(e) => handleSettingChange('bettingEnabled', e.target.checked)}
                    disabled={!canModify}
                  />
                  Enable Betting
                </label>
                <p className="setting-description">Allow users to place bets in this tournament</p>
              </div>

              <div className="setting-row">
                <div className="setting-item">
                  <label>Minimum Bet Amount</label>
                  <Input
                    type="number"
                    value={settings.minBetAmount}
                    onChange={(e) => handleSettingChange('minBetAmount', parseInt(e.target.value))}
                    disabled={!canModify}
                    min="1"
                    lightTheme={true}
                  />
                </div>
                <div className="setting-item">
                  <label>Maximum Bet Amount</label>
                  <Input
                    type="number"
                    value={settings.maxBetAmount}
                    onChange={(e) => handleSettingChange('maxBetAmount', parseInt(e.target.value))}
                    disabled={!canModify}
                    min="1"
                    lightTheme={true}
                  />
                </div>
              </div>

              <div className="setting-item">
                <label>Betting Deadline</label>
                <Input
                  type="datetime-local"
                  value={settings.bettingDeadline}
                  onChange={(e) => handleSettingChange('bettingDeadline', e.target.value)}
                  disabled={!canModify}
                  lang="en-US"
                  lightTheme={true}
                />
                <p className="setting-description">After this time, no new bets can be placed</p>
              </div>

              <div className="setting-item">
                <label>Maximum Players per User</label>
                <Input
                  type="number"
                  value={settings.maxPlayersPerUser}
                  onChange={(e) => handleSettingChange('maxPlayersPerUser', parseInt(e.target.value))}
                  disabled={!canModify}
                  min="1"
                  max="10"
                  lightTheme={true}
                />
                <p className="setting-description">How many different golfers each user can bet on</p>
              </div>

              <div className="setting-item">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={settings.allowMultipleBets}
                    onChange={(e) => handleSettingChange('allowMultipleBets', e.target.checked)}
                    disabled={!canModify}
                  />
                  Allow Multiple Bets on Same Player
                </label>
                <p className="setting-description">Let users place multiple bets on the same golfer</p>
              </div>
            </div>
          </Card>

          {/* Display Settings */}
          <Card className="settings-section">
            <h3>Display & Visibility</h3>
            
            <div className="setting-group">
              <div className="setting-item">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={settings.publicTournament}
                    onChange={(e) => handleSettingChange('publicTournament', e.target.checked)}
                    disabled={!canModify}
                  />
                  Public Tournament
                </label>
                <p className="setting-description">Make tournament visible to all users</p>
              </div>

              <div className="setting-item">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={settings.showLeaderboard}
                    onChange={(e) => handleSettingChange('showLeaderboard', e.target.checked)}
                    disabled={!canModify}
                  />
                  Show Leaderboard
                </label>
                <p className="setting-description">Display live leaderboard during tournament</p>
              </div>

              <div className="setting-item">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={settings.showPlayerStats}
                    onChange={(e) => handleSettingChange('showPlayerStats', e.target.checked)}
                    disabled={!canModify}
                  />
                  Show Player Statistics
                </label>
                <p className="setting-description">Show detailed player stats and performance</p>
              </div>

              <div className="setting-item">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={settings.showRealTimeUpdates}
                    onChange={(e) => handleSettingChange('showRealTimeUpdates', e.target.checked)}
                    disabled={!canModify}
                  />
                  Real-time Updates
                </label>
                <p className="setting-description">Push live updates to participants</p>
              </div>
            </div>
          </Card>

          {/* Notification Settings */}
          <Card className="settings-section">
            <h3>Notifications</h3>
            
            <div className="setting-group">
              <div className="setting-item">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={settings.emailNotifications}
                    onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                    disabled={!canModify}
                  />
                  Email Notifications
                </label>
                <p className="setting-description">Send tournament updates via email</p>
              </div>

              <div className="setting-item">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={settings.pushNotifications}
                    onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                    disabled={!canModify}
                  />
                  Push Notifications
                </label>
                <p className="setting-description">Send mobile push notifications</p>
              </div>

              <div className="setting-item">
                <label className="setting-label">
                  <input
                    type="checkbox"
                    checked={settings.reminderNotifications}
                    onChange={(e) => handleSettingChange('reminderNotifications', e.target.checked)}
                    disabled={!canModify}
                  />
                  Reminder Notifications
                </label>
                <p className="setting-description">Send betting deadline reminders</p>
              </div>
            </div>
          </Card>

          {/* Advanced Settings */}
          <Card className="settings-section">
            <h3>Advanced Configuration</h3>
            
            <div className="setting-group">
              <div className="setting-item">
                <label>Time Zone</label>
                <select
                  value={settings.timeZone}
                  onChange={(e) => handleSettingChange('timeZone', e.target.value)}
                  disabled={!canModify}
                  className="setting-select"
                >
                  <option value="America/New_York">Eastern Time (EST/EDT)</option>
                  <option value="America/Chicago">Central Time (CST/CDT)</option>
                  <option value="America/Denver">Mountain Time (MST/MDT)</option>
                  <option value="America/Los_Angeles">Pacific Time (PST/PDT)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>

              <div className="setting-item">
                <label>Scoring Frequency</label>
                <select
                  value={settings.scoringFrequency}
                  onChange={(e) => handleSettingChange('scoringFrequency', e.target.value)}
                  disabled={!canModify}
                  className="setting-select"
                >
                  <option value="real-time">Real-time</option>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                </select>
                <p className="setting-description">How often scores are updated</p>
              </div>

              <div className="setting-item">
                <label>Prize Distribution</label>
                <select
                  value={settings.prizeDistributionType}
                  onChange={(e) => handleSettingChange('prizeDistributionType', e.target.value)}
                  disabled={!canModify}
                  className="setting-select"
                >
                  <option value="automatic">Automatic</option>
                  <option value="manual">Manual</option>
                </select>
                <p className="setting-description">How prizes are distributed after tournament</p>
              </div>

              <div className="setting-item">
                <label>Tournament Category</label>
                <select
                  value={settings.category}
                  onChange={(e) => handleSettingChange('category', e.target.value)}
                  disabled={!canModify}
                  className="setting-select"
                >
                  <option value="professional">Professional</option>
                  <option value="amateur">Amateur</option>
                  <option value="mixed">Mixed</option>
                  <option value="special">Special Event</option>
                </select>
              </div>
            </div>
          </Card>

          {/* Cache Management */}
          <Card className="settings-section">
            <h3>Cache Management</h3>
            
            <div className="setting-group">
              <div className="setting-item">
                <label>Redis Cache</label>
                <p className="setting-description">
                  Clear all cached data to force fresh data retrieval. This may temporarily impact performance.
                </p>
                <Button
                  onClick={handleFlushCache}
                  disabled={isFlushingCache}
                  variant="danger"
                  className="flush-cache-btn"
                >
                  {isFlushingCache ? 'Flushing Cache...' : 'Flush All Cache'}
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Action Buttons */}
        {canModify && (
          <div className="settings-actions">
            <Button
              onClick={handleSave}
              disabled={!hasChanges || updateMutation.isLoading}
              className="save-btn"
            >
              {updateMutation.isLoading ? 'Saving...' : 'Save Settings'}
            </Button>
            
            <Button
              onClick={handleReset}
              disabled={!hasChanges}
              variant="secondary"
              className="reset-btn"
            >
              Reset Changes
            </Button>
          </div>
        )}

        {!canModify && (
          <div className="readonly-notice">
            <p>Settings cannot be modified because the tournament is COMPLETED or CANCELLED. Only DRAFT, ACTIVE, and LIVE tournaments can be edited.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TournamentSettingsTab;